import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import VerifiedIcon from "@mui/icons-material/Verified";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link, useNavigate } from "react-router-dom";
import { theme } from "../../theme/colors";
import {
  fetchCallLeaderboard,
  fetchUserCallActivity,
  getLeaderboardOptIn,
  setLeaderboardOptIn,
} from "../../utils/discoverApi";
import { buildBillSlug } from "../../utils/utils";
import { useUser } from "../../context/UserContext";

const PAGE_SIZE = 20;
const VERIFIED_GOLD = "#8B6914";

function formatBillCode(bill) {
  const type = String(bill.bill_type || bill.type || "").toUpperCase();
  const number = bill.bill_number ?? "";
  if (!type || number === "") return "";
  return `${type}.${number}`;
}

function getLeaderboardBillLabel(bill) {
  const code = formatBillCode(bill);
  const title = String(bill.title || "").trim();
  if (!code) return title || "Bill";
  if (!title || title === code || title === `${code.replace(".", " ")}`) {
    return code;
  }
  return `${code} – ${title}`;
}

function getBillLinkId(bill) {
  return buildBillSlug({
    type: bill.bill_type,
    bill_number: bill.bill_number,
    id: bill.bill_id,
  });
}

const linkSx = {
  color: theme.primary,
  fontWeight: 700,
  textDecoration: "underline",
  textUnderlineOffset: 2,
  "&:hover": { color: theme.primaryHover },
};

function LeaderboardRow({ entry, isCurrentUser, rowRef }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  const initials = (entry.display_name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const loadActivity = useCallback(async () => {
    if (activity || activityLoading) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const data = await fetchUserCallActivity(entry.user_id);
      setActivity(data);
    } catch {
      setActivityError("Could not load call activity.");
    } finally {
      setActivityLoading(false);
    }
  }, [activity, activityLoading, entry.user_id]);

  const handleToggleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      await loadActivity();
    }
  };

  return (
    <Box
      ref={rowRef}
      sx={{
        borderBottom: "1px solid #f1f5f9",
        borderLeft: isCurrentUser ? `3px solid ${theme.primary}` : "3px solid transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2.5,
          transition: "background 0.15s",
          "&:hover": { bgcolor: "#f8fafc" },
        }}
      >
        <Typography
          sx={{
            width: 28,
            flexShrink: 0,
            fontWeight: 800,
            fontSize: 15,
            color: entry.rank <= 3 ? theme.primary : "#64748b",
            textAlign: "center",
          }}
        >
          {entry.rank}
        </Typography>

        <Box
          component="button"
          onClick={() => navigate(`/profile/${entry.user_id}`)}
          aria-label={`View ${entry.display_name}'s profile`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
            border: "none",
            bgcolor: "transparent",
            cursor: "pointer",
            textAlign: "left",
            p: 0,
            fontFamily: "inherit",
            "&:hover .leaderboard-name": { color: theme.primary },
          }}
        >
          <Avatar
            src={entry.avatar_url || undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: `${theme.primary}20`,
              color: theme.primary,
              fontWeight: 700,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
              <Typography
                className="leaderboard-name"
                sx={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {entry.display_name}
              </Typography>
              {entry.is_verified && (
                <VerifiedIcon sx={{ fontSize: 16, color: VERIFIED_GOLD, flexShrink: 0 }} />
              )}
            </Box>
            {entry.username && (
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>@{entry.username}</Typography>
            )}
          </Box>
        </Box>

        <Chip
          icon={<PhoneInTalkIcon sx={{ fontSize: "14px !important" }} />}
          label={`${entry.call_count} ${entry.call_count === 1 ? "call" : "calls"}`}
          size="small"
          sx={{
            height: 28,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: `${theme.primary}15`,
            color: theme.primary,
            "& .MuiChip-icon": { color: theme.primary },
            flexShrink: 0,
          }}
        />

        <IconButton
          onClick={handleToggleExpand}
          aria-label={expanded ? "Hide bills called" : "Show bills called"}
          sx={{ width: 44, height: 44, color: "#64748b", flexShrink: 0 }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2, pl: { xs: 7, sm: 10 } }}>
          {activityLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={18} sx={{ color: theme.primary }} />
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading bills...</Typography>
            </Box>
          ) : activityError ? (
            <Typography sx={{ fontSize: 13, color: "#b45309", py: 1 }}>{activityError}</Typography>
          ) : activity?.bills?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {activity.total_bill_count > activity.bills.length && (
                <Typography sx={{ fontSize: 12, color: "#0f172a", pb: 0.25 }}>
                  Showing {activity.bills.length} most recent calls
                </Typography>
              )}
              {activity.bills.map((bill) => (
                <Box
                  key={`${bill.bill_id}-${bill.bill_type}-${bill.bill_number}`}
                  component="button"
                  onClick={() => navigate(`/bills/${getBillLinkId(bill)}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    textAlign: "left",
                    p: 1.25,
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    minHeight: 44,
                    "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {getLeaderboardBillLabel(bill)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <Chip
                      label={`${bill.count} ${bill.count === 1 ? "call" : "calls"}`}
                      size="small"
                      sx={{ height: 22, fontSize: 11, bgcolor: "#f1f5f9", color: "#334155", fontWeight: 600 }}
                    />
                    <ChevronRightIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#64748b", py: 1 }}>
              No bill-specific calls recorded yet.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function CallsLeaderboard({ isLoggedIn }) {
  const { userId } = useUser();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewerRank, setViewerRank] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);

  const [optedIn, setOptedIn] = useState(null);
  const [optInLoading, setOptInLoading] = useState(false);
  const [optInSaving, setOptInSaving] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(true);

  const currentUserRowRef = useRef(null);
  const pendingJumpRef = useRef(false);
  const requestTokenRef = useRef(0);
  const currentUserId = userId ? String(userId) : null;

  const scrollToCurrentUser = useCallback(() => {
    currentUserRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const loadLeaderboard = useCallback(async () => {
    if (!isLoggedIn) {
      setResults([]);
      setTotalPages(1);
      setViewerRank(null);
      setViewerPage(null);
      setLoading(false);
      setError(null);
      return;
    }

    const token = ++requestTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCallLeaderboard(page, PAGE_SIZE, { verifiedOnly });
      if (token !== requestTokenRef.current) return;
      setResults(data.results || []);
      setTotalPages(data.total_pages || 1);
      setViewerRank(data.viewer_rank ?? null);
      setViewerPage(data.viewer_page ?? null);
    } catch {
      if (token !== requestTokenRef.current) return;
      setError("Could not load the leaderboard. Please try again.");
    } finally {
      if (token === requestTokenRef.current) setLoading(false);
    }
  }, [page, isLoggedIn, verifiedOnly]);

  const handleVerifiedOnlyToggle = () => {
    setVerifiedOnly((prev) => !prev);
    setPage(1);
  };

  const handleJumpToMe = () => {
    if (!viewerPage) return;
    if (viewerPage !== page) {
      pendingJumpRef.current = true;
      setPage(viewerPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    scrollToCurrentUser();
  };

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    if (!pendingJumpRef.current || !currentUserId) return;
    const onPage = results.some((entry) => entry.user_id === currentUserId);
    if (onPage) {
      pendingJumpRef.current = false;
      requestAnimationFrame(() => scrollToCurrentUser());
    }
  }, [results, currentUserId, scrollToCurrentUser]);

  useEffect(() => {
    if (!isLoggedIn) {
      setOptedIn(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setOptInLoading(true);
      try {
        const data = await getLeaderboardOptIn();
        if (!cancelled) setOptedIn(Boolean(data.opted_in));
      } catch {
        if (!cancelled) setOptedIn(false);
      } finally {
        if (!cancelled) setOptInLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  const handleOptInToggle = async (event) => {
    const next = event.target.checked;
    setOptInSaving(true);
    try {
      const data = await setLeaderboardOptIn(next);
      setOptedIn(Boolean(data.opted_in));
      if (next) {
        await loadLeaderboard();
      }
    } catch {
      setError("Could not update leaderboard visibility. Please try again.");
    } finally {
      setOptInSaving(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          py: 4,
          px: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#334155", lineHeight: 1.5, textAlign: "center" }}>
          <Box component={Link} to="/auth/login" sx={linkSx}>
            Sign in
          </Box>
          {" or "}
          <Box component={Link} to="/auth/signup" sx={linkSx}>
            create an account
          </Box>
          {" to view the call leaderboard."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {!optInLoading && optedIn === false && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderBottom: "1px solid #bbf7d0",
            bgcolor: "#f0fdf4",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#065f46", mb: 0.25 }}>
              Show up on the leaderboard?
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#065f46", lineHeight: 1.45 }}>
              Let others see the calls you&apos;ve made to representatives.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 44 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#065f46" }}>
              {optInSaving ? "Saving..." : "Appear on leaderboard"}
            </Typography>
            <Switch
              checked={false}
              onChange={handleOptInToggle}
              disabled={optInSaving}
              inputProps={{ "aria-label": "Appear on call leaderboard" }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: theme.primary },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: theme.primary },
              }}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 1.5, sm: 2 },
          py: 0.75,
          borderBottom: "1px solid #f1f5f9",
          bgcolor: "#fff",
          minHeight: 44,
          flexWrap: "wrap",
          rowGap: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
            minHeight: 44,
          }}
        >
          {viewerRank ? (
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.4,
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              You&apos;re{" "}
              <Box
                component="button"
                onClick={handleJumpToMe}
                disabled={loading}
                aria-label={`Jump to rank ${viewerRank}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  bgcolor: "transparent",
                  p: "8px 4px",
                  m: 0,
                  minWidth: 44,
                  minHeight: 44,
                  font: "inherit",
                  fontSize: "inherit",
                  fontWeight: 800,
                  color: theme.primary,
                  cursor: loading ? "not-allowed" : "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                  verticalAlign: "middle",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": { color: theme.primaryHover, bgcolor: "transparent" },
                  "&:active": { bgcolor: "transparent" },
                  "&:focus": { bgcolor: "transparent" },
                  "&:focus-visible": { outline: `2px solid ${theme.primary}`, outlineOffset: 2, bgcolor: "transparent" },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                #{viewerRank}
              </Box>
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
            minHeight: 44,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.4,
              fontWeight: 600,
              color: "#0f172a",
              whiteSpace: "nowrap",
              flexShrink: 1,
            }}
          >
            Verified users
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <Switch
              size="small"
              checked={verifiedOnly}
              onChange={() => handleVerifiedOnlyToggle()}
              disabled={loading}
              inputProps={{ "aria-label": "Show verified users only" }}
              sx={{
                m: 0,
                "& .MuiSwitch-switchBase.Mui-checked": { color: theme.primary },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  bgcolor: theme.primary,
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <CircularProgress sx={{ color: theme.primary, mb: 2 }} />
            <Typography sx={{ fontWeight: 600, color: "#334155" }}>Loading leaderboard...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748b", mb: 3, textAlign: "center", maxWidth: 320 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadLeaderboard}
              sx={{
                borderRadius: "20px",
                fontWeight: 600,
                textTransform: "none",
                background: theme.gradient,
                "&:hover": { opacity: 0.9, background: theme.gradient },
              }}
            >
              Try Again
            </Button>
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <PhoneInTalkIcon sx={{ fontSize: 36, color: "#94a3b8" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a", mb: 1 }}>
              {verifiedOnly ? "No verified callers yet" : "No callers yet"}
            </Typography>
            <Typography sx={{ fontSize: 15, color: "#64748b", textAlign: "center", maxWidth: 300, lineHeight: 1.5 }}>
              {verifiedOnly
                ? "No verified users have opted in to the leaderboard yet."
                : "Be the first to call a representative and appear on the leaderboard."}
            </Typography>
          </Box>
        ) : (
          <>
            {results.map((entry) => {
              const isCurrentUser = currentUserId === entry.user_id;
              return (
                <LeaderboardRow
                  key={entry.user_id}
                  entry={entry}
                  isCurrentUser={isCurrentUser}
                  rowRef={isCurrentUser ? currentUserRowRef : null}
                />
              );
            })}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  p: 2.5,
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    color: page <= 1 ? "#cbd5e1" : "#334155",
                    "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155", px: 1 }}>
                  Page {page} of {totalPages}
                </Typography>
                <IconButton
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(totalPages, p + 1));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    color: page >= totalPages ? "#cbd5e1" : "#334155",
                    "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
